import { Tooltip } from 'react-tooltip';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Navbar } from './Navbar';
import { useEffect } from 'react';
import { useTour } from '@reactour/tour';
import { filesSteps } from '@/components/Tutorial/steps/filesSteps';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { dragAndDropSteps } from '@/components/Tutorial/steps/DragAndDropSteps';
import { useDispatch } from 'react-redux';
import {
  setHasCompletedAnalysisTutorial,
  setHasCompletedFilesTutorial,
} from '@/store/slices/tutorialSlice';
import { useAnalysisSteps } from '@/components/Tutorial/steps/analysisSteps';

export const RootLayout = () => {
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const viewerSteps = useAnalysisSteps();

  const isFilesPage = location.pathname.includes('files');
  const isFilePage = isFilesPage && id;

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { hasCompletedAnalysisTutorial, hasCompletedFilesTutorial } =
    useSelector((state: RootState) => state.tutorial);

  const showNavbar = !isFilePage;
  const bodyHeight = showNavbar ? 'min-h-[calc(100vh-64px)]' : 'min-h-full';

  const { setSteps, setCurrentStep, setIsOpen, currentStep, isOpen } =
    useTour();

  useEffect(() => {
    if (!isAuthenticated || !setSteps) {
      setIsOpen(false);
      setCurrentStep(0);
      return;
    }

    setCurrentStep(0);
    if (location.pathname === '/' || location.pathname === '/files') {
      if (hasCompletedAnalysisTutorial && hasCompletedFilesTutorial) {
        setSteps(dragAndDropSteps);
        setCurrentStep(0);
      } else {
        setSteps(filesSteps);
        setCurrentStep(0);
      }
    } else if (isFilePage) {
      setSteps(viewerSteps);
      setCurrentStep(0);
    } else {
      setSteps([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isFilePage,
    location.pathname,
    isOpen,
    setCurrentStep,
    setIsOpen,
    setSteps,
  ]);

  // Marquer le tutoriel d'analyse comme terminé quand est arrivé à la dernière étape
  useEffect(() => {
    if (isFilePage && currentStep === viewerSteps.length - 1) {
      dispatch(setHasCompletedAnalysisTutorial(true));
    }
  }, [isFilesPage, isFilePage, currentStep, dispatch, viewerSteps.length]);

  // Marquer le tutoriel de la page des fichiers comme terminé quand est arrivé à la dernière étape
  useEffect(() => {
    if (!isFilePage && currentStep === filesSteps.length - 1) {
      dispatch(setHasCompletedFilesTutorial(true));
    }
  }, [isFilesPage, isFilePage, currentStep, dispatch]);

  return (
    <div className="font-poppins min-h-screen bg-[#f4f5fb] dark:bg-[#1b1b1b]">
      <Navbar className="navbar" hidden={!showNavbar} />
      <div id="portal-root" />
      <div className={`${bodyHeight} main-content`}>
        <Tooltip
          id="tooltip"
          className="z-[10000] bg-[#cad2ef] !text-xs font-medium text-neutralText dark:bg-[#0d0d0d] dark:text-neutralText-dark"
        />
        <div className="min-h-[inherit] w-full">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
